import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import {
  PiXLight,
  PiReceiptLight,
  PiDownloadSimpleLight,
  PiArrowSquareOutLight,
} from "react-icons/pi";
import { Invoice } from "../../../store/actions/PlanActions";
import Spinner from '../TailwindComponents/Spinner';

interface InvoicesModalProps {
  open: boolean;
  onClose: () => void;
  invoices: Invoice[];
  loading?: boolean;
}

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    paid: "bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-400 border-success-200 dark:border-success-800",
    open: "bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-400 border-info-200 dark:border-info-800",
    draft: "bg-surface-sunken/70 text-fg-muted border-hairline",
    void: "bg-surface-sunken/70 text-fg-muted border-hairline",
    uncollectible:
      "bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-400 border-danger-200 dark:border-danger-800",
  };
  return map[status] || map.draft;
};

const formatCurrency = (amount: number, currency: string) => {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (timestamp: number) => {
  return new Date(timestamp * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const InvoicesModal: React.FC<InvoicesModalProps> = ({
  open,
  onClose,
  invoices,
  loading = false,
}) => {
  const { t } = useTranslation();

  return (
    <Transition show={open} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/20 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="glass w-full max-w-2xl rounded-lg shadow-lg overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-hairline">
                <div className="flex items-center gap-2">
                  <PiReceiptLight className="h-5 w-5 text-fg-strong" />
                  <Dialog.Title className="font-medium text-fg-strong text-sm">
                    {t("Invoices")}
                  </Dialog.Title>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded hover:bg-surface-sunken"
                >
                  <PiXLight className="h-4 w-4 text-fg-muted" />
                </button>
              </div>

              {/* Body */}
              <div className="max-h-[60vh] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Spinner size="glyph" className="!h-5 !w-5" />
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-12 px-5">
                    <PiReceiptLight className="h-8 w-8 mx-auto text-fg-muted mb-2" />
                    <p className="text-sm text-fg-muted">
                      {t("No invoices yet")}
                    </p>
                    <p className="text-xs text-fg-muted mt-1">
                      {t("Invoices will appear here after your first payment")}
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-hairline bg-surface-sunken/70">
                        <th className="text-start px-5 py-2.5 text-xs font-medium text-fg-muted uppercase tracking-wider">
                          {t("Date")}
                        </th>
                        <th className="text-start px-5 py-2.5 text-xs font-medium text-fg-muted uppercase tracking-wider">
                          {t("Invoice")}
                        </th>
                        <th className="text-start px-5 py-2.5 text-xs font-medium text-fg-muted uppercase tracking-wider">
                          {t("Amount")}
                        </th>
                        <th className="text-start px-5 py-2.5 text-xs font-medium text-fg-muted uppercase tracking-wider">
                          {t("Status")}
                        </th>
                        <th className="text-end px-5 py-2.5 text-xs font-medium text-fg-muted uppercase tracking-wider">
                          {t("Actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-hairline-soft">
                      {invoices.map((inv) => (
                        <tr
                          key={inv.id}
                          className="hover:bg-surface-sunken/70 transition-colors"
                        >
                          <td className="px-5 py-3 text-fg-strong whitespace-nowrap">
                            {formatDate(inv.created)}
                          </td>
                          <td className="px-5 py-3">
                            <p className="text-fg-strong font-medium">
                              {inv.number || "—"}
                            </p>
                            {inv.lines.length > 0 && (
                              <p className="text-xs text-fg-muted mt-0.5 truncate max-w-[200px]">
                                {inv.lines.map((l) => l.description).join(", ")}
                              </p>
                            )}
                          </td>
                          <td className="px-5 py-3 text-fg-strong whitespace-nowrap font-medium">
                            {formatCurrency(inv.amount_due, inv.currency)}
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(
                                inv.status
                              )}`}
                            >
                              {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-end whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              {inv.hosted_invoice_url && (
                                <a
                                  href={inv.hosted_invoice_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 rounded hover:bg-surface-sunken text-fg-muted hover:text-fg-strong transition-colors"
                                  title={t("View")}
                                >
                                  <PiArrowSquareOutLight className="h-4 w-4" />
                                </a>
                              )}
                              {inv.invoice_pdf && (
                                <a
                                  href={inv.invoice_pdf}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 rounded hover:bg-surface-sunken text-fg-muted hover:text-fg-strong transition-colors"
                                  title={t("Download PDF")}
                                >
                                  <PiDownloadSimpleLight className="h-4 w-4" />
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer */}
              {invoices.length > 0 && (
                <div className="px-5 py-3 border-t border-hairline bg-surface-sunken/70 text-xs text-fg-muted">
                  {`${t("Showing")} ${invoices.length} ${t("invoice(s)")}`}
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default InvoicesModal;
